import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import _isEmpty from 'lodash/isEmpty';
import _pick from 'lodash/pick';
import { TaskTimelineFrameEnum } from 'graphql-common';
import { Values } from '@lib/interfaces/form';
import PageBody from '@lib/layouts/DashboardLayout/PageBody/PageBody';
import WhiteBox from '@lib/components/WhiteBox/WhiteBox';
import Modal from '@lib/components/Modal/Modal';
import Form, { FormRef } from '@lib/components/ReactHookForm/FormContainer';
import formStyles from '@lib/assets/styles/Form.module.scss';
import Chip, { ChipSizes, ChipTypes } from '@lib/components/Chip/Chip';
import _without from 'lodash/without';
import { InputSizes } from '@lib/components/Input/enums';
import useModalManagement, { Modals } from '@lib/hooks/useModalManagement';
import { revertTransformDate, transformDate } from '@lib/utils/dateTimeHelpers';
import Button, {
  ButtonSizes,
  ButtonTypes,
} from '@lib/components/Button/Button';
import IconButton, {
  IconButtonTypes,
} from '@lib/components/IconButton/IconButton';
import Select from '@lib/components/Select/Select';
import DatePicker from '@lib/components/DateTimePicker/DatePicker';
import getDateRangeLabel from '@lib/utils/getDateRangeLabel';
import CheckBoxSingleBool from '@lib/components/CheckBoxSingleBool/CheckBoxSingleBool';
import {
  datePickerQuarterFormat,
  datePickerShortDateFormat,
  datePickerYearFormat,
} from '@lib/enums/dateTime';
import { getTaskTimelineViewOptions } from 'utils/getTaskTimelineViewOptions';
import TasksFilterFieldNames from '@lib/enums/fieldNames/tasksFilterFieldNames';
import {
  getDecrementedNextEndDate,
  getIncrementedNextEndDate,
  getIsDecrementDisabled,
  getIsIncrementDisabled,
  getMaxDate,
  getMinDate,
  getPeriodEndDate,
  getPeriodStartDate,
} from './utils/getPeriodDate';
import useQueries from './utils/useQueries';
import styles from './TasksTimelineFilter.module.scss';
import useFilterFields, {
  TASKS_TIMELINE_FILTER_EMPTY_VALUES,
} from './utils/useFilterFields';
import TasksTimelineFilterActions from './TasksTimelineFilterActions';

const filterModal = Modals.DashboardTimelineFilterModal;
const modals = [filterModal];

type Props = {
  resetFilterParams: () => void;
  onUpdateFilterParams: (values: Values) => void;
  filterParams: Values;
  endDate?: string | null;
  isFullTableView: boolean;
  onToggleFullTableView: () => void;
  onEndDateChange: (v: string) => void;
  onViewChange: (v: unknown) => void;
  periodEndParam?: string | null;
  periodStartParam?: string | null;
  view: TaskTimelineFrameEnum;
};

export default function TasksTimelineFilter(props: Props) {
  const {
    resetFilterParams,
    filterParams,
    onUpdateFilterParams,
    endDate,
    isFullTableView,
    onToggleFullTableView,
    onEndDateChange,
    onViewChange,
    periodEndParam,
    periodStartParam,
    view,
  } = props;
  const { t } = useTranslation();
  const formRef = useRef<FormRef>(undefined);
  const { getFormCategoriesLoadOptions, getSitesLoadOptions } = useQueries();

  const useModalManagementResult = useModalManagement(modals);
  const { isModalOpened, openModal, closeModal } = useModalManagementResult;
  const openFilter = () => openModal(filterModal);
  const closeFilter = () => closeModal(filterModal);
  const clearFilter = () => {
    closeModal(filterModal);
    resetFilterParams();
  };

  const viewOptions = getTaskTimelineViewOptions();
  const selectedViewOption = viewOptions.find(({ value }) => value === view);

  const datePickerType = (() => {
    if (view === TaskTimelineFrameEnum.Year) {
      return 'year';
    }
    if (view === TaskTimelineFrameEnum.Quarter) {
      return 'quarter';
    }
    if (view === TaskTimelineFrameEnum.Month) {
      return 'month';
    }
    if (view === TaskTimelineFrameEnum.Week) {
      return 'week';
    }
    return undefined;
  })();

  const datePickerFormat = (() => {
    if (view === TaskTimelineFrameEnum.Year) {
      return datePickerYearFormat;
    }
    if (view === TaskTimelineFrameEnum.Quarter) {
      return datePickerQuarterFormat;
    }
    return datePickerShortDateFormat;
  })();

  // Week has no single-unit label like "May 2026" — show the day range instead.
  const datePickerDisplayValue =
    endDate && view === TaskTimelineFrameEnum.Week
      ? getDateRangeLabel(
          getPeriodStartDate(endDate, view),
          getPeriodEndDate(endDate, view),
        )
      : undefined;

  const isDecrementDisabled = getIsDecrementDisabled(
    endDate,
    periodStartParam,
    view,
  );

  const isIncrementDisabled = getIsIncrementDisabled(
    endDate,
    periodEndParam,
    view,
  );

  const navMinDate = getMinDate(periodStartParam, view);

  const navMaxDate = getMaxDate(periodEndParam, view);

  const decrementEndDate = () => {
    if (endDate) {
      const nextEndDate = getDecrementedNextEndDate(
        revertTransformDate(endDate),
        view,
      );
      if (nextEndDate) onEndDateChange(transformDate(nextEndDate, true));
    }
  };

  const incrementEndDate = () => {
    if (endDate) {
      const nextEndDate = getIncrementedNextEndDate(
        revertTransformDate(endDate),
        view,
      );
      if (nextEndDate) onEndDateChange(transformDate(nextEndDate, true));
    }
  };

  const onDateChange = (v: string) => {
    const nextEndDate = v
      ? transformDate(getPeriodEndDate(v, view), true)
      : transformDate(getPeriodEndDate(new Date().toISOString(), view), true);
    if (nextEndDate) {
      onEndDateChange(nextEndDate);
    }
  };

  const defaultValues =
    filterParams && Object.keys(filterParams).length > 0 ? filterParams : {};

  const filterCounter = Object.values(
    _pick(
      defaultValues,
      TasksFilterFieldNames.Site,
      TasksFilterFieldNames.FormCategory,
      TasksFilterFieldNames.ValidityHints,
    ),
  ).filter((value) => {
    if (Array.isArray(value)) {
      return !!_without(value, '').length;
    }
    return !_isEmpty(value);
  }).length;

  const fields = useFilterFields({
    getFormCategoriesLoadOptions,
    getSitesLoadOptions,
    t,
  });

  const onSubmit = (values: Values) => {
    onUpdateFilterParams(values);
    closeFilter();
  };

  return (
    <>
      <PageBody className={styles.filterWrap}>
        <WhiteBox
          className={styles.filter}
          verticalPaddings="extraSmall"
          horizontalPaddings="small"
        >
          <div className={styles.filterRow}>
            <Chip
              counter={filterCounter}
              label={t('filter')}
              leftIcon="filter_alt"
              leftIconSymbolsOutlined
              onClick={openFilter}
              size={ChipSizes.large}
              type={ChipTypes.chipFilter}
              tooltipForCroppedTextDisabled
            />
            {!!filterCounter && (
              <Button
                buttonSize={ButtonSizes.large}
                buttonText={t('reset')}
                buttonType={ButtonTypes.primaryText}
                onClick={clearFilter}
              />
            )}
            <CheckBoxSingleBool
              name="show-task-details"
              value={isFullTableView}
              options={[{ label: 'checked', value: 'checked' }]}
              onChange={onToggleFullTableView}
              label={t('show-task-details')}
            />
          </div>
          <div className={styles.navRow}>
            <div className={styles.datePickerWrap}>
              <IconButton
                icon="chevron_left"
                rounded
                type={IconButtonTypes.SecondaryFilled}
                onClick={decrementEndDate}
                disabled={isDecrementDisabled}
              />
              <div>
                <DatePicker
                  name="datePicker"
                  format={datePickerFormat}
                  displayValue={datePickerDisplayValue}
                  onChange={onDateChange}
                  pickerType={datePickerType}
                  size={InputSizes.medium}
                  value={
                    endDate
                      ? transformDate(getPeriodStartDate(endDate, view))
                      : undefined
                  }
                  minDate={navMinDate}
                  maxDate={navMaxDate}
                />
              </div>
              <IconButton
                icon="chevron_right"
                rounded
                type={IconButtonTypes.SecondaryFilled}
                onClick={incrementEndDate}
                disabled={isIncrementDisabled}
              />
            </div>
            <div>
              <Select
                name="view"
                value={selectedViewOption}
                isSearchable={false}
                isClearable={false}
                size={InputSizes.medium}
                onChange={onViewChange}
                options={viewOptions}
                menuPlacement="bottom"
                usePortal
              />
            </div>
          </div>
        </WhiteBox>
      </PageBody>
      <Modal
        isOpen={isModalOpened(filterModal)}
        caption={t('filter')}
        onClose={closeFilter}
      >
        <Form
          formRef={formRef}
          fields={fields}
          onSubmit={onSubmit}
          defaultValues={defaultValues}
          actionsComponent={TasksTimelineFilterActions}
          actionsComponentProps={{
            onCancel: closeFilter,
          }}
          formClassName={formStyles.form}
        />
      </Modal>
    </>
  );
}
